export type ServiceWorkerCallbacks = {
  onRegistered?: (registration: ServiceWorkerRegistration) => void;
  onUpdateFound?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: unknown) => void;
};

const SPEAKING_TOPICS_URL = '/ielts_data/speaking/ielts_topics_final_filtered.json';
const WRITING_CARDS_URL = '/ielts_data/writing/writingSeedCards.json';
const LISTENING_INTENSIVE_URLS = Object.values(
  import.meta.glob('../../ielts_data/listening/**/*_intensive.json', {
    eager: true,
    import: 'default',
    query: '?url',
  })
) as string[];

const TODAY_PRELOAD_URLS = [SPEAKING_TOPICS_URL, WRITING_CARDS_URL, ...LISTENING_INTENSIVE_URLS];

export function canUseServiceWorker(
  mode: string,
  hasServiceWorker: boolean,
  hasWindow: boolean = typeof window !== 'undefined'
): boolean {
  return hasWindow && hasServiceWorker && mode !== 'development';
}

export function registerServiceWorker(
  mode: string = import.meta.env.MODE,
  swUrl: string = '/sw.js',
  callbacks: ServiceWorkerCallbacks = {}
): Promise<ServiceWorkerRegistration | null> {
  if (!canUseServiceWorker(mode, 'serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const doRegister = () => {
      const sw = navigator.serviceWorker;
      sw
        .register(swUrl)
        .then((registration) => {
          callbacks.onRegistered?.(registration);
          if (registration.installing) {
            callbacks.onUpdateFound?.(registration);
          } else if (registration.addEventListener) {
            registration.addEventListener('updatefound', () => callbacks.onUpdateFound?.(registration));
          }

          // Preload datasets needed for the "Today" dashboard to work offline.
          enqueueTodayPrefetch(registration);
          if (sw?.ready && typeof sw.ready.then === 'function') {
            sw.ready
              .then((readyReg) => enqueueTodayPrefetch(readyReg))
              .catch(() => undefined);
          }

          resolve(registration);
        })
        .catch((error) => {
          callbacks.onError?.(error);
          console.error('[PWA] Service worker registration failed', error);
          resolve(null);
        });
    };

    if (document.readyState === 'complete') {
      doRegister();
    } else {
      window.addEventListener('load', doRegister, { once: true });
    }
  });
}

function enqueueTodayPrefetch(registration: ServiceWorkerRegistration) {
  const worker = registration.active || registration.waiting || registration.installing;
  if (!worker || typeof worker.postMessage !== 'function') return;

  const urls = TODAY_PRELOAD_URLS.map((url) => new URL(url, location.origin).toString());
  worker.postMessage({ type: 'PREFETCH_URLS', urls });
}
